import { doc, setDoc, addDoc, collection, serverTimestamp, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const SESSION_STORAGE_KEY = "egodziny_registration_session";

const safeParse = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const createSessionId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `reg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
};

const getIsoTimestamp = () => new Date().toISOString();

const getDeviceInfo = () => {
  if (typeof navigator === "undefined") return {};
  const ua = navigator.userAgent;

  let deviceType = "desktop";
  if (/Mobi|Android|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua)) {
    deviceType = "mobile";
  } else if (/iPad|Tablet|PlayBook|Silk/i.test(ua)) {
    deviceType = "tablet";
  }

  let browser = "unknown";
  if (/Edg\//i.test(ua)) browser = "Edge";
  else if (/OPR\/|Opera/i.test(ua)) browser = "Opera";
  else if (/Chrome\/\d/i.test(ua)) browser = "Chrome";
  else if (/Firefox\/\d/i.test(ua)) browser = "Firefox";
  else if (/Safari\/\d/i.test(ua)) browser = "Safari";
  else if (/MSIE|Trident/i.test(ua)) browser = "IE";

  return { deviceType, browser };
};

export const getRegistrationSessionId = () => {
  if (typeof window === "undefined") return null;
  const stored = safeParse(localStorage.getItem(SESSION_STORAGE_KEY));
  return stored?.id || null;
};

export const ensureRegistrationSessionId = () => {
  if (typeof window === "undefined") return null;
  const stored = safeParse(localStorage.getItem(SESSION_STORAGE_KEY));
  if (stored?.id) return stored.id;

  const sessionId = createSessionId();
  const startedAt = getIsoTimestamp();
  try {
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ id: sessionId, startedAt }));
  } catch (error) {
    console.warn("Unable to persist registration session.", error);
  }
  return sessionId;
};

export const startRegistrationSession = async (context = {}) => {
  if (typeof window === "undefined") return null;
  const existing = safeParse(localStorage.getItem(SESSION_STORAGE_KEY));
  const sessionId = existing?.id || createSessionId();
  const startedAt = existing?.startedAt || getIsoTimestamp();

  if (!existing?.id) {
    try {
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify({ id: sessionId, startedAt }));
    } catch (error) {
      console.warn("Unable to persist registration session.", error);
    }
  }

  try {
    await setDoc(
      doc(db, "registration_sessions", sessionId),
      {
        createdAt: serverTimestamp(),
        startedAt,
        lastEventAt: serverTimestamp(),
        lastEventTimestamp: startedAt,
        lastEventType: "session_start",
        ...getDeviceInfo(),
        ...context
      },
      { merge: true }
    );
  } catch (error) {
    console.warn("Registration session init failed.", error);
  }

  return sessionId;
};

export const trackRegistrationEvent = async (type, data = {}) => {
  if (!type) return;
  const sessionId = getRegistrationSessionId();
  if (!sessionId) return;

  try {
    await addDoc(
      collection(db, "registration_sessions", sessionId, "events"),
      {
        type,
        createdAt: serverTimestamp(),
        timestamp: getIsoTimestamp(),
        ...data
      }
    );
    const sessionUpdate = {
      lastEventAt: serverTimestamp(),
      lastEventTimestamp: getIsoTimestamp(),
      lastEventType: type
    };
    if (type === "submit_success") {
      sessionUpdate.isCompleted = true;
      sessionUpdate.completedAt = serverTimestamp();
      sessionUpdate.completedTimestamp = getIsoTimestamp();
    }
    await setDoc(
      doc(db, "registration_sessions", sessionId),
      sessionUpdate,
      { merge: true }
    );
  } catch (error) {
    console.warn("Registration event tracking failed.", error);
  }
};

export const trackRegistrationField = (field, value, context = {}) => {
  if (!field) return;
  const trimmed = typeof value === "string" ? value.trim() : value;
  if (trimmed === "" || trimmed === null || typeof trimmed === "undefined") return;
  trackRegistrationEvent("field_update", { field, value: trimmed, ...context });
};

export const isRegistrationSessionFlagged = async (sessionId) => {
  if (!sessionId) return false;
  try {
    const snap = await getDoc(doc(db, "registration_sessions", sessionId));
    return snap.exists() ? !!snap.data()?.isFlagged : false;
  } catch {
    return false;
  }
};

export const trackRegistrationSelection = (field, value, context = {}) => {
  if (!field) return;
  if (value === "" || value === null || typeof value === "undefined") return;
  trackRegistrationEvent("selection_update", { field, value, ...context });
};
